import type { ReactNode } from "react";
import { useCurrentUser, userHasGroup, type CurrentUser } from "@/lib/auth";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";

export interface RequireAdminProps {
  children: ReactNode;
  group?: string;
  /** Ersetzt die Gruppenprüfung, z. B. Büro oder App-Admins. */
  allow?: (user: CurrentUser) => boolean;
  deniedMessage?: ReactNode;
}

/**
 * Verhindert das Rendern der Kinder, bis `/me` geladen und die Gruppe geprüft ist.
 * Innerhalb des persistent gerenderten `Layout` zu verwenden – daher KEIN eigenes
 * Vollbild-Layout mehr.
 */
export function RequireAdmin({
  children,
  group = "App-Admins",
  allow,
  deniedMessage,
}: RequireAdminProps) {
  const { user, loading, error } = useCurrentUser();

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-label-secondary">
        <div className="flex items-center gap-2 text-sm">
          <Spinner />
          <span>Sitzung wird geprüft …</span>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="mx-auto max-w-md py-10">
        <Alert variant="error" title="Sitzung konnte nicht geladen werden">
          {error || "Bitte erneut anmelden."}
        </Alert>
      </div>
    );
  }

  const permitted = allow ? allow(user) : userHasGroup(user, group);
  if (!permitted) {
    return (
      <div className="mx-auto max-w-md py-10">
        <Alert variant="error" title="Keine Berechtigung">
          {deniedMessage ?? (
            <>
              Diese Seite ist auf die Gruppe <strong>{group}</strong> beschränkt.
            </>
          )}
        </Alert>
      </div>
    );
  }

  return <>{children}</>;
}
